import { Component, computed, input, linkedSignal, model, OnChanges, output, signal, SimpleChanges, viewChild } from "@angular/core";
import { PaginationChangesInterface, PaginationComponentInterface } from "../../../interfaces/pagination.interface";
import { RppOptionsInterface } from "../../interfaces/rpp-options.interface";
import { FormsModule } from "@angular/forms";
import { TranslatePipe } from "@ngx-translate/core";
import { OverlayPanelComponent } from "@kernel/shared/modules/overlay-panel/components/overlay-panel/overlay-panel.component";
import { OverlayMenuListComponent } from "@kernel/shared/modules/overlay-panel/components/overlay-menu-list/overlay-menu-list.component";
import { SvgIconComponent } from "@kernel/tools/svg-icon/components/svg-icon/svg-icon.component";
import { OverlayMenuItemComponent } from "@kernel/shared/modules/overlay-panel/components/overlay-menu-item/overlay-menu-item.component";

@Component({
  selector: "sxw-pagination",
  templateUrl: "./pagination.component.html",
  styleUrls: ["./pagination.component.scss"],
  imports: [TranslatePipe, FormsModule, SvgIconComponent, OverlayPanelComponent, OverlayMenuListComponent, OverlayMenuItemComponent],
  host: {
    class: "pagination pagination-container",
  },
})
export class PaginationComponent implements PaginationComponentInterface, OnChanges {
  total = model<number>(0);
  rppOptions = input<RppOptionsInterface[]>([
    { label: "20", value: 20 },
    { label: "30", value: 30 },
    { label: "40", value: 40 },
  ]);
  showArrows = input(true);
  showStartEndArrows = input(false);
  showPageNumberInput = input(true);

  rpp = linkedSignal(() => this.rppOptions()[0].value);
  page = signal(1);
  pageNumber = signal(this.page());
  totalPages = signal(0);
  totalClipPages = signal(0);

  /**
   * Returns the current page/
   */
  getPage = computed(() => this.page());
  getTotalPages = computed(() => this.totalPages());

  overlay = viewChild<OverlayPanelComponent>("overlay");

  onPaginationChange = output<PaginationChangesInterface>();

  ngOnChanges(changes: SimpleChanges) {
    if (changes["rppOptions"]) {
      this.onPaginationChange.emit({ page: this.getPage(), rpp: this.rpp() });
    }
  }

  goToPrevPage() {
    if (this.page() > 1) {
      this.changePage(this.page() - 1);
    }
  }

  goToNextPage() {
    if (this.page() + 1 <= this.totalPages()) {
      this.changePage(this.page() + 1);
    }
  }

  goToFirstPage() {
    if (this.page() > 1) {
      this.changePage(1);
    }
  }

  goToLastPage() {
    if (this.page() < this.totalPages()) {
      this.changePage(this.totalPages());
    }
  }

  /**
   * Sets the total results.
   * @param total
   */
  setTotal(total: number): void {
    this.total.set(total);
    this.totalPages.set(Math.ceil(this.total() / this.rpp())); // 50 / 20 = 3
    this.totalClipPages.set(
      Math.ceil(
        this.totalPages() - (this.totalPages() - (this.page() + 1)) / 2 // 3 - (3 - (1 + 1)) / 2 = Math.ceil(2.5)
      )
    );
  }

  /**
   * Runs when the user changes the results per page.
   */
  changeRPP(rpp: RppOptionsInterface): void {
    this.rpp.set(rpp.value);
    this.page.set(1);
    this.overlay()?.popover.hide();
    this.onPaginationChange.emit({ page: this.page(), rpp: this.rpp() });
  }

  /**
   * Runs when the user changes the current page.
   * @param page
   */
  changePage(page: number, emit = true): void {
    this.page.set(page);
    this.pageNumber.set(page);
    if (emit) {
      this.onPaginationChange.emit({ page: this.page(), rpp: this.rpp() });
    }
  }

  resetPagination(emit = true) {
    this.page.set(1);
    this.rpp.set(this.rppOptions()[0].value);
    if (emit) {
      this.onPaginationChange.emit({ page: this.page(), rpp: this.rpp() });
    }
  }

  handleInput(pageNumber: any) {
    this.pageNumber.set(parseInt(pageNumber));
    if (this.pageNumber() > 0 && this.pageNumber() <= this.totalPages() && this.pageNumber != this.page) {
      this.changePage(this.pageNumber());
      this.pageNumber = this.page;
    }
    if (this.pageNumber() > this.totalPages()) {
      this.goToLastPage();
      this.pageNumber = this.page;
    }
  }
}
